import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cs_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 — clear token and reset auth state
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      localStorage.removeItem('cs_token');
      localStorage.removeItem('cs_refresh_token');
      const { useAuth } = require('./store');
      useAuth.getState().logout();
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register: (d) => api.post('/auth/register', d),
  login: (d) => api.post('/auth/login', d),
  me: () => api.get('/auth/me'),
  forgotPassword: (d) => api.post('/auth/forgot-password', d),
  resetPassword: (d) => api.post('/auth/reset-password', d),
  verifyEmail: (d) => api.post('/auth/verify-email', d),
  resendVerifyPublic: (d) => api.post('/auth/resend-verify-public', d),
};

export const qrApi = {
  claimFreeInserts: (d) => api.post('/qr/claim-free', d),
  myInserts: () => api.get('/qr/mine'),
};

export const stolenRegistryApi = {
  publicList: (q) => api.get('/safety/stolen-registry/public', { params: q ? { q } : {} }),
};

// Public cert lookup — anyone can paste a PSA/BGS/SGC/CSG/HGA cert
// and see if it's been claimed in Card Shop. Backs /lookup. The API
// returns { already_claimed, slab, catalog_match, provider_configured,
// provider_error }; the page also cross-checks the cert against the
// public stolen registry.
//
// raw() does the same job for numbered parallels: catalog row +
// serial number is as unique as a cert, so the public lookup
// surface treats them as equivalent identifiers.
export const lookupApi = {
  cert: (company, cert_number) =>
    api.post('/catalog/cert-lookup', { company, cert_number }),
  raw: (card_catalog_id, serial_number) =>
    api.post('/catalog/raw-lookup', { card_catalog_id, serial_number }),
};

// Public catalog search — used by the raw-card mode on /lookup to
// resolve "Wyatt Langford 2024 Bowman Chrome blue refractor" into
// a specific card_catalog_id before the serial-number check.
export const catalogApi = {
  search: (q) => api.get('/catalog/search', { params: { q, limit: 8 } }),
};

// Binders — own + public
export const bindersApi = {
  mine: () => api.get('/binders'),
  get: (id) => api.get(`/binders/${id}`),
  update: (id, data) => api.patch(`/binders/${id}`, data),
  publicByLinkToken: (linkToken) => api.get(`/b/${linkToken}`),
};

// Cards
export const cardsApi = {
  mine: (params) => api.get('/cards/mine', { params }),
  get: (id) => api.get(`/cards/${id}`),
};

// Offers — make + browse + accept/decline/counter
export const offersApi = {
  mine: () => api.get('/offers/mine'),
  get: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  accept: (id) => api.post(`/offers/${id}/accept`),
  decline: (id, reason) => api.post(`/offers/${id}/decline`, { reason }),
  counter: (id, data) => api.post(`/offers/${id}/counter`, data),
  message: (id, body) => api.post(`/offers/${id}/messages`, { body }),
};

// Trust signals (E5) — surface bad-actor flags pre-offer
export const safetyApi = {
  trustFlags: (idOrUsername) => api.get(`/safety/trust-flags/${encodeURIComponent(idOrUsername)}`),
  submitSupportTicket: (data) => api.post('/safety/support', data),
};

// Marketplace (public browse). All endpoints accept unauthenticated
// callers and return active listings only.
export const marketplaceApi = {
  search: (params) => api.get('/marketplace/search', { params }),
  justListed: (params) => api.get('/marketplace/just-listed', { params }),
  mostWatched: (params) => api.get('/marketplace/most-watched', { params }),
};

export const listingsApi = {
  get: (id) => api.get(`/listings/${id}`),
};
